type StorageItem<T> = {
  timeToLive: number;
  value: T;
};

export enum CacheTTL {
  ONE_SECOND = 1000,
  ONE_HOUR = 3600,
  ONE_DAY = 24 * CacheTTL.ONE_HOUR,
  ONE_MONTH = 30 * CacheTTL.ONE_DAY,
  ONE_YEAR = 12 * CacheTTL.ONE_MONTH,
}

/**
 * Enforce a cache cleanup mechanism for breaking changes that need to ensure
 * a clean state of `localStorage` in order to avoid bugs after users are updated.
 *
 * Changing `CACHE_REVISION` will cause `localStorage` to be flushed entirely: at
 * the very least, this means the user will be logged out.
 * Only items whose `key` starts with any of the values in `CACHE_KEEP_PREFIXES` will be kept.
 *
 * Use with caution.
 */
export const CACHE_REVISION_KEY = '__revision__';
export const CACHE_REVISION = '2';

const CACHE_KEEP_PREFIXES = [
  'graphiql:', // Preserve existing content of the API explorer
];

export class CacheService {
  private static instance: CacheService | null = null;

  private constructor() {
    const revision = localStorage.getItem(CACHE_REVISION_KEY);
    if (revision !== CACHE_REVISION) {
      for (const key in localStorage) {
        const kept = CACHE_KEEP_PREFIXES.find((p) => key.startsWith(p));
        if (!kept) {
          localStorage.removeItem(key);
        }
      }
      localStorage.setItem(CACHE_REVISION_KEY, CACHE_REVISION);
    }
  }

  static getInstance(): CacheService {
    if (CacheService.instance === null) {
      CacheService.instance = new CacheService();
    }

    return CacheService.instance;
  }

  setItem<T>(key: string, value: T, timeToLive = CacheTTL.ONE_HOUR) {
    const storageItem = {
      value,
      timeToLive: Date.now() / 1000 + timeToLive,
    };

    localStorage.setItem(key, JSON.stringify(storageItem));
  }

  clearItem(key: string) {
    localStorage.removeItem(key);
  }

  getItem<T>(key: string, defaultValue: T | null = null) {
    const rawStorageItem = localStorage.getItem(key);

    if (rawStorageItem === null) {
      return defaultValue;
    }

    const storageItem = JSON.parse(rawStorageItem) as StorageItem<T>;

    if (Date.now() / 1000 > storageItem.timeToLive) {
      return defaultValue;
    }

    return storageItem.value;
  }
}
